/**
 * Access cases in My Tickets (6 Oct, product owner): one row per case for
 * everyone involved, its Ivanti requests grouped under it.
 *
 * The server sends the reader's cases with every request each is made of
 * (`GET /access/mine`); this decides which rows a list shows for its filters,
 * which tickets the rows stand in for (hidden from the ticket list, matched by
 * ticket id), and what the reader is asked to do. Pure, so
 * `tests/access-rows.test.mjs` runs it without React.
 */

import type { AccessCaseSummary } from "@/lib/api";

import type { TicketBucket, TicketKind, TicketStatusCode } from "../api/contract";

/** A row of My Tickets: one of the reader's tickets, or an access case. */
export type TicketListRow<T> =
  | { kind: "ticket"; id: string; ticket: T }
  | { kind: "access"; id: string; access: AccessCaseSummary };

type CaseRequests = Pick<AccessCaseSummary, "requests">;

/** Ticket ids compare as the server mints them, but case-insensitively. */
function idKey(ticketId: string): string {
  return ticketId.trim().toLowerCase();
}

/** Every request an access case groups: these are listed under its row, not on their own. */
export function groupedTicketIds(cases: readonly CaseRequests[] | undefined): Set<string> {
  const ids = new Set<string>();
  for (const item of cases ?? []) {
    for (const request of item.requests ?? []) {
      ids.add(idKey(request.ticket_id));
    }
  }
  return ids;
}

/** The tickets that are not part of an access case the reader follows as a row. */
export function withoutGrouped<T extends { ticket_id: string }>(
  tickets: readonly T[],
  grouped: ReadonlySet<string>,
): T[] {
  return grouped.size === 0
    ? [...tickets]
    : tickets.filter((ticket) => !grouped.has(idKey(ticket.ticket_id)));
}

type ListedCase = Pick<
  AccessCaseSummary,
  "archived" | "action_count" | "person_name" | "case_number" | "requests"
>;

export interface AccessRowFilter {
  bucket: TicketBucket;
  status: readonly TicketStatusCode[];
  kind: TicketKind | null;
  q: string;
  page: number;
}

/** Whether a case belongs in a bucket: open ones in Open, finished ones in Resolved/Closed. */
export function caseInBucket(
  item: Pick<ListedCase, "archived" | "action_count">,
  bucket: TicketBucket,
) {
  const archived = item.archived === true;
  switch (bucket) {
    case "OPEN":
      return !archived;
    case "AWAITING_ME":
      return !archived && (item.action_count ?? 0) > 0;
    case "RESOLVED":
    case "CLOSED":
      return archived;
    default:
      return true;
  }
}

/**
 * The cases a My Tickets list shows above its tickets, for its filters.
 *
 * Page one only (the tickets are paged by the server, the cases are not); none
 * under a status filter (those are ticket statuses) or an incident filter (a
 * case is made of service requests); the search matches the person, the case
 * number and its requests' numbers.
 */
export function accessRowsFor<C extends ListedCase>(
  cases: readonly C[] | undefined,
  filter: AccessRowFilter,
): C[] {
  if (filter.page > 1 || filter.status.length > 0 || filter.kind === "INCIDENT") {
    return [];
  }
  const q = filter.q.trim().toLowerCase();
  return (cases ?? []).filter(
    (item) =>
      caseInBucket(item, filter.bucket) &&
      (q === "" ||
        [item.person_name, item.case_number, ...(item.requests ?? []).map((r) => r.ticket_number)]
          .join(" ")
          .toLowerCase()
          .includes(q)),
  );
}

/** The access rows first (needing the reader first, as the server orders them), then the tickets. */
export function mergeTicketRows<T extends { ticket_id: string }>(
  cases: readonly AccessCaseSummary[],
  tickets: readonly T[],
): TicketListRow<T>[] {
  return [
    ...cases.map((access) => ({ kind: "access" as const, id: `access:${access.case_id}`, access })),
    ...tickets.map((ticket) => ({ kind: "ticket" as const, id: ticket.ticket_id, ticket })),
  ];
}

export type AccessActionKey =
  | "access.actionApprove"
  | "access.actionGrant"
  | "access.actionRejected"
  | "access.actionForYou";

/** "Action for you: Approve" when approving is among the reader's steps; null when none. */
export function accessActionKey(
  item: Pick<AccessCaseSummary, "action_count" | "your_actions">,
): AccessActionKey | null {
  if ((item.action_count ?? 0) === 0) {
    return null;
  }
  const actions = item.your_actions ?? [];
  if (actions.includes("approve")) {
    return "access.actionApprove";
  }
  // 19 Oct: an approver rejected a step the reader sent; they follow it up.
  if (actions.includes("rejected")) {
    return "access.actionRejected";
  }
  if (actions.length === 1 && actions[0] === "grant") {
    return "access.actionGrant";
  }
  return "access.actionForYou";
}

/** The rail's split: cases waiting on the reader, live ones, and recently finished ones. */
export function splitAccessCases<C extends Pick<ListedCase, "archived" | "action_count">>(
  cases: readonly C[] | undefined,
): { waiting: C[]; inProgress: C[]; finished: C[] } {
  const all = cases ?? [];
  return {
    waiting: all.filter((c) => c.archived !== true && (c.action_count ?? 0) > 0),
    inProgress: all.filter((c) => c.archived !== true && (c.action_count ?? 0) === 0),
    finished: all.filter((c) => c.archived === true),
  };
}
